import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, thumbUrl, getVaultToken, clearVaultToken } from "@/lib/api";

export default function Vault() {
  const [sections, setSections] = useState([]);
  const [media, setMedia] = useState([]);
  const [activeSection, setActiveSection] = useState("all");
  const [activeTag, setActiveTag] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const scrollerRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!getVaultToken()) {
      nav("/portfolio", { replace: true });
      return;
    }
    let cancelled = false;
    Promise.all([api.listVaultSections(), api.listVaultMedia()])
      .then(([s, m]) => { if (!cancelled) { setSections(s); setMedia(m); } })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          clearVaultToken();
          nav("/portfolio", { replace: true });
        } else {
          setError("Failed to load vault");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [nav]);

  const allTags = useMemo(() => {
    const set = new Set();
    media.forEach(m => m.tags?.forEach(t => set.add(t)));
    return Array.from(set);
  }, [media]);

  const populatedSections = useMemo(() => {
    const slugs = new Set(media.map(m => m.section_slug));
    return sections.filter(s => slugs.has(s.slug));
  }, [sections, media]);

  const filtered = useMemo(() => {
    return media.filter(m => {
      if (activeSection !== "all" && m.section_slug !== activeSection) return false;
      if (activeTag !== "all" && !(m.tags || []).includes(activeTag)) return false;
      return true;
    });
  }, [media, activeSection, activeTag]);

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * window.innerWidth, behavior: "smooth" });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (lightboxIdx !== null) {
        if (e.key === "Escape") setLightboxIdx(null);
        if (e.key === "ArrowRight") setLightboxIdx(i => Math.min(filtered.length - 1, (i ?? 0) + 1));
        if (e.key === "ArrowLeft") setLightboxIdx(i => Math.max(0, (i ?? 0) - 1));
      } else {
        if (e.key === "ArrowRight") scrollBy(1);
        if (e.key === "ArrowLeft") scrollBy(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, filtered.length]);

  const exit = () => {
    clearVaultToken();
    nav("/portfolio", { replace: true });
  };

  return (
    <div className="grain min-h-screen bg-[var(--fsyl-bg)] text-[var(--fsyl-text)] relative" data-testid="vault-page">
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-black/40 border-b border-white/5">
        <div className="flex items-center justify-between px-6 md:px-10 py-4">
          <div className="flex items-center gap-6">
            <div className="font-display font-black text-xl tracking-tight">FYSL</div>
            <div className="font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-cyan)]">/ VAULT</div>
            <div className="hidden md:block font-mono-fsyl text-[10px] tracking-[0.3em] text-[var(--fsyl-dim)]">
              {filtered.length.toString().padStart(3, "0")} ENTRIES
            </div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar max-w-[55%]">
            <VChip active={activeSection === "all"} onClick={() => setActiveSection("all")} testid="vault-filter-section-all">ALL</VChip>
            {populatedSections.map(s => (
              <VChip
                key={s.id}
                active={activeSection === s.slug}
                onClick={() => setActiveSection(s.slug)}
                testid={`vault-filter-section-${s.slug}`}
              >
                {s.name}
              </VChip>
            ))}
          </div>
          <button onClick={exit} data-testid="vault-exit" className="font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-dim)] hover:text-[var(--fsyl-text)] transition">
            ← EXIT
          </button>
        </div>
        {allTags.length > 0 && (
          <div className="px-6 md:px-10 pb-3 flex items-center gap-2 overflow-x-auto hide-scrollbar">
            <span className="font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-dim)] mr-2">TAGS</span>
            <VChip small active={activeTag === "all"} onClick={() => setActiveTag("all")} testid="vault-filter-tag-all">ALL</VChip>
            {allTags.map(t => (
              <VChip key={t} small active={activeTag === t} onClick={() => setActiveTag(t)} testid={`vault-filter-tag-${t}`}>
                {t}
              </VChip>
            ))}
          </div>
        )}
      </header>

      <main className="pt-28">
        {loading ? (
          <div className="h-[calc(100vh-7rem)] flex items-center justify-center font-mono-fsyl text-[12px] tracking-widest text-[var(--fsyl-muted)]" data-testid="vault-loading">
            UNLOCKING VAULT...
          </div>
        ) : error ? (
          <div className="h-[calc(100vh-7rem)] flex items-center justify-center font-mono-fsyl text-red-400">{error}</div>
        ) : filtered.length === 0 ? (
          <VaultEmpty hasSections={sections.length > 0} />
        ) : (
          <>
            <div ref={scrollerRef} className="snap-container flex overflow-x-auto overflow-y-hidden h-[calc(100vh-7rem)] hide-scrollbar">
              <VaultIntro count={filtered.length} sections={sections} />
              {(() => {
                const pages = [];
                let lastSection = null;
                let chapterNum = 0;
                filtered.forEach((m, i) => {
                  if (m.section_slug !== lastSection) {
                    chapterNum += 1;
                    const sec = sections.find(s => s.slug === m.section_slug);
                    pages.push(
                      <VaultChapterIntro
                        key={`v-chapter-${m.section_slug}-${i}`}
                        number={chapterNum}
                        name={sec?.name || m.section_slug}
                        isFirst={lastSection === null}
                      />
                    );
                    lastSection = m.section_slug;
                  }
                  pages.push(
                    <VaultPage key={m.id} item={m} index={i} total={filtered.length} onOpen={() => setLightboxIdx(i)} />
                  );
                });
                pages.push(<VaultClosing key="v-closing" />);
                return pages;
              })()}
            </div>

            <button onClick={() => scrollBy(-1)} data-testid="vault-prev" className="fixed left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 border border-white/10 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] transition font-mono-fsyl text-sm" aria-label="Previous">←</button>
            <button onClick={() => scrollBy(1)} data-testid="vault-next" className="fixed right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 border border-white/10 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)] transition font-mono-fsyl text-sm" aria-label="Next">→</button>
          </>
        )}
      </main>

      {lightboxIdx !== null && filtered[lightboxIdx] && (
        <VaultLightbox
          item={filtered[lightboxIdx]}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx(i => Math.max(0, i - 1))}
          onNext={() => setLightboxIdx(i => Math.min(filtered.length - 1, i + 1))}
          atStart={lightboxIdx === 0}
          atEnd={lightboxIdx === filtered.length - 1}
        />
      )}
    </div>
  );
}

const VChip = ({ children, active, onClick, testid, small }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`font-mono-fsyl ${small ? "text-[9px] px-2 py-1" : "text-[10px] px-3 py-1.5"} tracking-[0.25em] uppercase whitespace-nowrap border transition ${
      active
        ? "border-[var(--fsyl-cyan)] text-[var(--fsyl-cyan)]"
        : "border-white/10 text-[var(--fsyl-muted)] hover:border-white/30 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const VaultIntro = ({ count, sections }) => (
  <section className="snap-page shrink-0 w-screen h-full flex items-center px-8 md:px-24" data-testid="vault-intro">
    <div className="max-w-3xl fade-up">
      <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-6">
        / VAULT
      </div>
      <h2 className="font-display font-light text-5xl md:text-7xl tracking-tighter leading-[0.95]">
        For the people<br/>
        <span className="text-[var(--fsyl-muted)] italic font-reflection font-normal">I love.</span>
      </h2>
    </div>
  </section>
);

const VaultClosing = () => (
  <section className="snap-page shrink-0 w-screen h-full flex items-center justify-center px-8" data-testid="vault-closing">
    <div className="text-center fade-up">
      <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-dim)] mb-6">END OF VAULT</div>
      <h2 className="font-display font-black text-6xl md:text-8xl tracking-tighter mb-6">FYSL</h2>
      <p className="font-reflection italic text-xl text-[var(--fsyl-muted)]">Glad you&apos;re here.</p>
    </div>
  </section>
);

const VaultChapterIntro = ({ number, name, isFirst }) => {
  const num = String(number).padStart(2, "0");
  return (
    <section className="snap-page shrink-0 w-screen h-full flex items-center px-8 md:px-24" data-testid={`vault-chapter-intro-${name.toLowerCase()}`}>
      <div className="max-w-3xl fade-up">
        <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-6">
          {isFirst ? `CHAPTER ${num}` : `CHAPTER ${num} / NEXT`}
        </div>
        <h2 className="font-display font-light text-5xl md:text-7xl tracking-tighter leading-[0.95] mb-6">
          {name}
        </h2>
        <div className="font-mono-fsyl text-[9px] tracking-widest text-[var(--fsyl-dim)]">
          — TURN THE PAGE —
        </div>
      </div>
    </section>
  );
};

const VaultPage = ({ item, index, total, onOpen }) => {
  const displaySrc = item.kind === "image" ? thumbUrl(item.id, "vault", 1600) : fileUrl(item.id, "vault");
  return (
    <section className="snap-page shrink-0 w-screen h-full flex flex-col md:flex-row items-stretch px-6 md:px-16 py-8 md:py-12 gap-8 md:gap-16" data-testid={`vault-page-${item.id}`}>
      <div className="flex-1 flex items-center justify-center min-h-0">
        <button onClick={onOpen} className="block w-full h-full max-h-[78vh] group relative flex items-center justify-center" data-testid={`vault-open-lightbox-${item.id}`}>
          {item.kind === "video" ? (
            <video src={displaySrc} className="max-w-[640px] w-full max-h-[60vh] object-contain img-hover" controls playsInline preload="none" />
          ) : (
            <img src={displaySrc} alt={item.title || "Untitled"} className="w-full h-full max-h-[78vh] object-contain img-hover" loading="lazy" decoding="async" />
          )}
        </button>
      </div>

      <aside className="w-full md:w-[36%] md:max-w-md flex flex-col justify-center md:max-h-[80vh] md:overflow-y-auto pr-1">
        <div className="font-mono-fsyl text-[10px] tracking-[0.35em] text-[var(--fsyl-cyan)] mb-4">
          {String(index + 1).padStart(3, "0")} / {String(total).padStart(3, "0")} — {item.section_slug.toUpperCase()}
        </div>
        {item.title && (
          <h3 className="font-display font-medium text-2xl md:text-3xl tracking-tight leading-tight mb-4">{item.title}</h3>
        )}
        {item.reflection ? (
          <p className="font-reflection text-base md:text-lg text-[var(--fsyl-text)]/85 leading-relaxed whitespace-pre-line">
            {item.reflection}
          </p>
        ) : null}

        <div className="mt-6 pt-4 border-t border-white/10 space-y-1.5 font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)]">
          {item.camera && <div><span className="text-[var(--fsyl-dim)]">CAMERA / </span>{item.camera.toUpperCase()}</div>}
          {item.captured_at && <div><span className="text-[var(--fsyl-dim)]">CAPTURED / </span>{item.captured_at.toUpperCase()}</div>}
          {(item.tags?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {item.tags.map(t => (
                <span key={t} className="px-2 py-0.5 border border-white/10 text-[9px]">{t}</span>
              ))}
            </div>
          )}
        </div>
      </aside>
    </section>
  );
};

const VaultLightbox = ({ item, onClose, onPrev, onNext, atStart, atEnd }) => {
  const src = fileUrl(item.id, "vault");
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="vault-lightbox">
      <div className="flex justify-between items-center p-6 font-mono-fsyl text-[10px] tracking-widest text-[var(--fsyl-muted)]">
        <span>{item.section_slug.toUpperCase()}{item.title ? ` / ${item.title.toUpperCase()}` : ""}</span>
        <button onClick={onClose} data-testid="vault-lightbox-close" className="hover:text-[var(--fsyl-cyan)]">✕ CLOSE</button>
      </div>
      <div className="flex-1 flex items-center justify-center px-8 pb-8 relative">
        <button disabled={atStart} onClick={onPrev} data-testid="vault-lightbox-prev" className="absolute left-4 disabled:opacity-20 font-mono-fsyl border border-white/10 w-10 h-10 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)]">←</button>
        {item.kind === "video" ? (
          <video src={src} className="max-w-[900px] w-full max-h-[80vh]" controls autoPlay playsInline />
        ) : (
          <img src={src} alt={item.title || "Untitled"} className="max-h-full max-w-full object-contain" />
        )}
        <button disabled={atEnd} onClick={onNext} data-testid="vault-lightbox-next" className="absolute right-4 disabled:opacity-20 font-mono-fsyl border border-white/10 w-10 h-10 hover:border-[var(--fsyl-cyan)] hover:text-[var(--fsyl-cyan)]">→</button>
      </div>
    </div>
  );
};

const VaultEmpty = ({ hasSections }) => (
  <div className="h-[calc(100vh-7rem)] flex flex-col items-center justify-center text-center px-8" data-testid="vault-empty">
    <div className="font-mono-fsyl text-[10px] tracking-[0.4em] text-[var(--fsyl-cyan)] mb-6">VAULT EMPTY</div>
    <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter mb-6">No private frames yet.</h2>
    <p className="font-reflection text-xl text-[var(--fsyl-muted)] max-w-xl">
      {hasSections
        ? "Upload from the admin panel and pick a vault section as the destination."
        : "Create a vault section in the admin panel, then upload media to it."}
    </p>
  </div>
);
